import { useTranslations } from 'next-intl';
import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import useOnClickOutside from '@//hooks/useOnClickOutside';

export type SectionType = 'geoLocation' | 'vehicle' | 'article' | 'history';

export interface Section<T> {
  type: SectionType;
  label: string;
  items: T[];
}

export interface UseSuggestionFoldoutManagerOptions<G, V, A, H> {
  geoLocationSuggestions: G[];
  vehicleSuggestions: V[];
  articleSuggestions: A[];
  filterHistory: H[];
  searchQuery: string;
  queryString?: string;
  clickOutsideRef?: RefObject<HTMLElement | null>;
}

export interface FlattenedItem<G, V, A, H> {
  type: SectionType;
  item: G | V | A | H;
  sectionIndex: number;
  itemIndex: number;
}

export interface UseSuggestionFoldoutManagerReturn<G, V, A, H> {
  activeIndex: number;
  activeSection: SectionType | null;
  activeItem: FlattenedItem<G, V, A, H> | null;
  sections: Readonly<[Section<G>, Section<V>, Section<A>, Section<H>]>;
  showDropdown: boolean;
  onFocus: () => void;
  goToNext: () => void;
  goToPrev: () => void;
  resetSuggestions: () => void;
  resetActiveIndex: () => void;
}

const useSuggestionFoldoutManager = <G, V, A, H>({
  geoLocationSuggestions,
  vehicleSuggestions,
  articleSuggestions,
  filterHistory,
  searchQuery,
  queryString,
  clickOutsideRef,
}: UseSuggestionFoldoutManagerOptions<G, V, A, H>): UseSuggestionFoldoutManagerReturn<
  G,
  V,
  A,
  H
> => {
  const t = useTranslations('utils.suggestionFoldoutManager');
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [activeSection, setActiveSection] = useState<SectionType | null>(null);
  const [inputFocused, setInputFocused] = useState<boolean>(false);

  const sections = useMemo(
    () =>
      [
        {
          type: 'geoLocation' as const,
          label: t('geoLocation.label'),
          items: searchQuery ? geoLocationSuggestions : [],
        },
        {
          type: 'vehicle' as const,
          label: t('vehicle.label'),
          items: searchQuery ? vehicleSuggestions : [],
        },
        {
          type: 'article' as const,
          label: t('articles.label'),
          items: searchQuery ? articleSuggestions : [],
        },
        {
          type: 'history' as const,
          label: t('history.label'),
          items: filterHistory,
        },
      ] as const,
    [searchQuery, geoLocationSuggestions, vehicleSuggestions, articleSuggestions, filterHistory],
  );

  const flattenedItems = useMemo(() => {
    const result: FlattenedItem<G, V, A, H>[] = [];
    sections.forEach((section, sectionIndex) => {
      section.items.forEach((item, itemIndex) => {
        result.push({ type: section.type, item, sectionIndex, itemIndex });
      });
    });
    return result;
  }, [sections]);

  const showDropdown = useMemo(
    () =>
      inputFocused &&
      ((Boolean(searchQuery) && (vehicleSuggestions.length > 0 || articleSuggestions.length > 0)) ||
        geoLocationSuggestions.length > 0 ||
        (!searchQuery && filterHistory.length > 0)),
    [
      inputFocused,
      searchQuery,
      geoLocationSuggestions.length,
      vehicleSuggestions.length,
      articleSuggestions.length,
      filterHistory.length,
    ],
  );

  useEffect(() => {
    setActiveIndex(-1);
    setActiveSection(null);
  }, [
    searchQuery,
    queryString,
    geoLocationSuggestions.length,
    vehicleSuggestions.length,
    articleSuggestions.length,
    filterHistory.length,
  ]);

  const onFocus = useCallback(() => setInputFocused(true), []);

  const goToNext = useCallback(() => {
    if (flattenedItems.length > 0) {
      setActiveIndex((prev) => {
        const next = prev === flattenedItems.length - 1 || prev === -1 ? 0 : prev + 1;
        setActiveSection(flattenedItems[next].type);
        return next;
      });
    }
  }, [flattenedItems]);

  const goToPrev = useCallback(() => {
    if (flattenedItems.length > 0) {
      setActiveIndex((prev) => {
        const next = prev <= 0 ? flattenedItems.length - 1 : prev - 1;
        setActiveSection(flattenedItems[next].type);
        return next;
      });
    }
  }, [flattenedItems]);

  const resetSuggestions = useCallback(() => {
    setInputFocused(false);
    setActiveIndex(-1);
    setActiveSection(null);
  }, []);

  const resetActiveIndex = useCallback(() => {
    setActiveIndex(-1);
    setActiveSection(null);
  }, []);

  useOnClickOutside(clickOutsideRef, resetSuggestions);

  const activeItem = useMemo(
    () => (activeIndex !== -1 && flattenedItems[activeIndex] ? flattenedItems[activeIndex] : null),
    [activeIndex, flattenedItems],
  );

  return {
    activeIndex,
    activeSection,
    activeItem,
    sections,
    showDropdown,
    onFocus,
    goToNext,
    goToPrev,
    resetSuggestions,
    resetActiveIndex,
  };
};

export default useSuggestionFoldoutManager;
